import { Controller } from "@hotwired/stimulus";
import { fillSpec, looksLikeColor } from "../lib/fill_cases";

/**
 * La couleur selon l'annonce, dans le panneau Remplissage (plan media-template-generators
 * §4.4) : des cas — une condition (éditeur embarqué), une couleur — et la couleur du calque
 * sinon. Les cas sont rendus par le serveur depuis le binding `fill` ; ici on les relit dans
 * le DOM et on pose le binding sur le canvas (lib fill_cases : l'expression, et les cas en
 * provenance). Un cas sans condition ne compte pas encore.
 *
 * La couleur du calque (le picker au-dessus, `shape:set-fill`) est le « sinon » : la
 * changer réécrit le binding ; passer en dégradé le retire.
 *
 * Naming: "fill-cases" in HTML.
 */
export default class extends Controller {
  static targets = ["cases", "caseTemplate", "otherwise"];

  static values = { default: String, subjectTokens: Array, debounce: { type: Number, default: 400 } };

  disconnect() {
    clearTimeout(this._timer);
  }

  addCase() {
    this.casesTarget.append(this.caseTemplateTarget.content.firstElementChild.cloneNode(true));
    this.otherwiseTarget.hidden = false;
  }

  removeCase(event) {
    event.currentTarget.closest("[data-case]").remove();
    this.otherwiseTarget.hidden = !this.casesTarget.querySelector("[data-case]");
    this.sync();
  }

  pickColor(event) {
    const kase = event.currentTarget.closest("[data-case]");
    kase.querySelector("[data-case-hex]").value = event.currentTarget.value;
    this.sync();
  }

  typeColor(event) {
    const kase = event.currentTarget.closest("[data-case]");
    if (/^#[0-9a-f]{6}$/i.test(event.currentTarget.value)) kase.querySelector("[data-case-color]").value = event.currentTarget.value;
    this.sync();
  }

  // Le picker du calque : une couleur unie devient le « sinon », un dégradé retire les cas
  defaultChanged(event) {
    const value = String(event.detail?.value ?? "");
    if (value.startsWith("linear-gradient(")) return this.unbind();
    if (!looksLikeColor(value)) return;

    this.defaultValue = value;
    if (this.readCases().length) this.sync();
  }

  sync() {
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.apply(), this.debounceValue);
  }

  readCases() {
    return Array.from(this.casesTarget.querySelectorAll("[data-case]")).map((kase) => ({
      when: kase.querySelector("[data-condition-editor-target='code']").value.trim(),
      value: kase.querySelector("[data-case-hex]").value.trim(),
    }));
  }

  apply() {
    this._timer = null;
    const spec = fillSpec(this.readCases(), this.defaultValue, this.subjectTokensValue);
    if (!spec) return this.unbind();

    window.dispatchEvent(new CustomEvent("text:bind", { detail: { field: "fill", ...spec } }));
  }

  unbind() {
    window.dispatchEvent(new CustomEvent("text:unbind", { detail: { field: "fill" } }));
  }
}
